import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalTag from '../hooks/useCanonicalTag';
import useMetaTags from '../hooks/useMetaTags';
import { getLeagueDetails } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/fonts.css';
import '../styles/league_detail.css';
import '../styles/teams.css';
import '../styles/championships.css';

function seasonLabel(year, quarter) {
    if (!year) return null;
    return quarter ? `${year} S${quarter}` : `${year}`;
}

const LeagueDetail = () => {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const locale = i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US';
    const [league, setLeague] = useState(null);
    usePageTitle(league?.name);
    useCanonicalTag();
    useMetaTags({
        title: league ? `${league.name} | Celeritas` : 'Celeritas',
        description: league?.about || 'Join an iRacing league on Celeritas. Create and manage your iRacing leagues, championships and races in one place.',
        image: league?.logo_file_url || league?.small_logo || league?.logo,
        url: `https://celeritas.live/leagues/${id}`
    });
    const [championships, setChampionships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchLeagueData = async () => {
            try {
                const leagueData = await getLeagueDetails(id);
                setLeague(leagueData);
                const standalone = (leagueData.championships || []).filter(c => !c.series_id);
                setChampionships(standalone);
            } catch (err) {
                console.error("Erro ao carregar liga:", err);
                setError(t('leagueDetail.error'));
            }
        };

        const loadData = async () => {
            setLoading(true);
            await fetchLeagueData();
            setLoading(false);
        };

        loadData();
    }, [id]);

    if (loading) {
        return (
            <div className="LeagueDetailPage">
                <Header />
                <main><p>{t('leagueDetail.loading')}</p></main>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className="LeagueDetailPage">
                <Header />
                <main><p className="error-message">{error}</p></main>
                <Footer />
            </div>
        );
    }

    if (!league) {
        return (
            <div className="LeagueDetailPage">
                <Header />
                <main><p>{t('leagueDetail.notFound')}</p></main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="LeagueDetailPage">
            <Header />

            <main>
                <div className="content-grid">
                    <section className="center-col league-detail-content">
                        <section className="league-info-bar">
                            <div className="league-info-left">
                                <div className="league-header">
                                    <img src={league.logo_file_url || league.small_logo || league.logo} alt={league.name} className="league-logo-header" />
                                    <div>
                                        <h1 style={{ marginBottom: league.has_password ? 6 : 0 }}>{league.name}</h1>
                                        {league.has_password && (
                                            <span title={t('nav.verifiedTooltip')} style={{
                                                fontSize: '0.75rem', fontWeight: 600,
                                                background: '#1a3d2b', color: '#4caf50',
                                                border: '1px solid #2d6b3a', borderRadius: 4,
                                                padding: '2px 9px', cursor: 'default', display: 'inline-block',
                                            }}>✓ {t('nav.verifiedBadge')}</span>
                                        )}
                                    </div>
                                </div>
                                <p className="league-about-text">{league.about || t('leagueDetail.noDescription')}</p>

                                {(league.youtube_url || league.discord_url || league.whatsapp_url || league.instagram_url || league.twitch_url) && (
                                    <div className="league-social-links">
                                        {league.youtube_url && (
                                            <a href={league.youtube_url} target="_blank" rel="noopener noreferrer" className="social-icon-link social-youtube" aria-label="YouTube">
                                                <svg viewBox="0 0 24 24" className="social-icon-svg">
                                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                                </svg>
                                            </a>
                                        )}
                                        {league.discord_url && (
                                            <a href={league.discord_url} target="_blank" rel="noopener noreferrer" className="social-icon-link social-discord" aria-label="Discord">
                                                <svg viewBox="0 0 24 24" className="social-icon-svg">
                                                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.012.043.023.06a19.9 19.9 0 0 0 5.993 3.029.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.029.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                                                </svg>
                                            </a>
                                        )}
                                        {league.whatsapp_url && (
                                            <a href={league.whatsapp_url} target="_blank" rel="noopener noreferrer" className="social-icon-link social-whatsapp" aria-label="WhatsApp">
                                                <svg viewBox="0 0 24 24" className="social-icon-svg">
                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                                                </svg>
                                            </a>
                                        )}
                                        {league.instagram_url && (
                                            <a href={league.instagram_url} target="_blank" rel="noopener noreferrer" className="social-icon-link social-instagram" aria-label="Instagram">
                                                <svg viewBox="0 0 24 24" className="social-icon-svg">
                                                    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12c0 3.259.014 3.668.072 4.948.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.689.072-4.948 0-3.259-.014-3.667-.072-4.947-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                                                </svg>
                                            </a>
                                        )}
                                        {league.twitch_url && (
                                            <a href={league.twitch_url} target="_blank" rel="noopener noreferrer" className="social-icon-link social-twitch" aria-label="Twitch">
                                                <svg viewBox="0 0 24 24" className="social-icon-svg">
                                                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
                                                </svg>
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="league-info-right">
                                <p><strong>{t('leagueDetail.owner')}</strong> {league.owner?.display_name || 'N/A'}</p>
                                <p><strong>{t('leagueDetail.createdAt')}</strong> {league.created_at ? new Date(league.created_at).toLocaleDateString(locale) : 'N/A'}</p>
                                <p><strong>{t('leagueDetail.members')}</strong> {league.roster_count || 'N/A'}</p>
                                <p><strong>{t('leagueDetail.website')}</strong> {league.url ? <a href={league.url} target="_blank" rel="noopener noreferrer">{league.url}</a> : t('leagueDetail.doesNotExist')}</p>
                            </div>
                        </section>

                        <div className="league-organizer-bar">
                            <Link to={`/leagues/${league.league_id}/organizer`} className="btn-organizer">
                                {t('leagueDetail.organizerPanel')}
                            </Link>
                            {!league.has_password && (
                                <Link to={`/leagues/${league.league_id}/claim`} className="btn-organizer" style={{ marginLeft: '8px', background: '#1a3a1a', borderColor: '#2d6b35', color: '#5cc96e' }}>
                                    {t('leagueDetail.claimLeague')}
                                </Link>
                            )}
                        </div>

                        <hr />

                        {/* Series */}
                        {(() => {
                            const visibleSeries = league.series_groups || [];
                            if (visibleSeries.length === 0) return null;
                            return (
                                <div>
                                    <h3 style={{ color: '#e0e6ed', marginBottom: '12px' }}>{t('common.series')}</h3>
                                    <div className="teams-grid">
                                        {visibleSeries.map(serie => {
                                            const filtered = serie.championships || [];
                                            const active = filtered.filter(c => c.active).length;
                                            const finished = filtered.filter(c => !c.active).length;
                                            return (
                                                <article className="team-card" key={serie.id}>
                                                    <div className="team-info-main">
                                                        <h4>
                                                            <Link to={`/leagues/${league.league_id}/series/${serie.id}`}>{serie.name}</Link>
                                                        </h4>
                                                        <div className="team-stats-mini">
                                                            <span><i className="fas fa-trophy"></i> {filtered.length} {t('leagueDetail.seasons')}</span>
                                                            {active > 0 && <span className="status-live">{active} {t('leagueDetail.activeSeasons')}</span>}
                                                            {finished > 0 && <span>{finished} {t('leagueDetail.finishedSeasons')}</span>}
                                                        </div>
                                                    </div>
                                                    <Link to={`/leagues/${league.league_id}/series/${serie.id}`} className="btn-profile">{t('common.viewSeries')}</Link>
                                                </article>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Standalone championships */}
                        {(() => {
                            const filtered = championships;
                            const active = filtered.filter(c => c.active);
                            const finished = filtered.filter(c => !c.active);
                            if (filtered.length === 0) return null;

                            const ChampCard = ({ champ }) => (
                                <article className="team-card" key={champ.championship_id}>
                                    <div className="team-info-main">
                                        <h4>
                                            <Link to={`/championships/${champ.championship_id}`}>{champ.name}</Link>
                                        </h4>
                                        <div className="team-stats-mini">
                                            {seasonLabel(champ.season_year, champ.season_quarter) && (
                                                <span className="season-badge">{seasonLabel(champ.season_year, champ.season_quarter)}</span>
                                            )}
                                            <span className={champ.active ? 'status-live' : 'status-finished'}>
                                                {champ.active ? t('common.active') : t('common.finished')}
                                            </span>
                                        </div>
                                    </div>
                                    <Link to={`/championships/${champ.championship_id}`} className="btn-profile">{t('common.viewStandings')}</Link>
                                </article>
                            );

                            return (
                                <>
                                    {active.length > 0 && (
                                        <div style={{ marginTop: '20px' }}>
                                            <h3 style={{ color: '#e0e6ed', marginBottom: '12px' }}>{t('leagueDetail.otherChampionships')}</h3>
                                            <div className="teams-grid">
                                                {active.map(champ => <ChampCard key={champ.championship_id} champ={champ} />)}
                                            </div>
                                        </div>
                                    )}
                                    {finished.length > 0 && (
                                        <div style={{ marginTop: '20px' }}>
                                            <details>
                                                <summary style={{
                                                    color: '#9ba3ab', fontSize: '14px', cursor: 'pointer',
                                                    userSelect: 'none', listStyle: 'none', display: 'flex',
                                                    alignItems: 'center', gap: '6px', marginBottom: '12px',
                                                }}>
                                                    <span style={{ fontSize: '12px' }}>▶</span>
                                                    {t('leagueDetail.finishedChampionships')} ({finished.length})
                                                </summary>
                                                <div className="teams-grid" style={{ marginTop: '12px' }}>
                                                    {finished.map(champ => <ChampCard key={champ.championship_id} champ={champ} />)}
                                                </div>
                                            </details>
                                        </div>
                                    )}
                                </>
                            );
                        })()}

                        {(league.series_groups || []).length === 0 && championships.length === 0 && (
                            <p>{t('leagueDetail.noChampionships')}</p>
                        )}
                    </section>

                </div>
            </main>

            <Footer />
        </div>
    );
};

export default LeagueDetail;
